import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Flex,
  Grid,
  GridItem,
  Heading,
  Text,
  TextProps,
} from "@chakra-ui/react";

import React from "react";
import { Image } from "../../components";

interface TicketSummaryProps {
  movieDetails: TicketSummaryMovieDetails;
  seats: string[];
  children?: React.ReactNode;
  headingText?: string;
  headingAction?: React.ReactNode;
  ticketSummaryBoxProps?: BoxProps;
  headingBoxProps?: BoxProps;
  headingTitleProps?: BoxProps;
  detailContainerProps?: BoxProps;
  gridProps?: BoxProps;
  posterGridItemProps?: BoxProps;
  posterImageProps?: BoxProps;
  movieDetailsGridItemProps?: BoxProps;
  movieTitleProps?: BoxProps;
  cinemaHallProps?: BoxProps;
  showDateTimeProps?: BoxProps;
  seatProps?: BoxProps;
  seatLabelProps?: BoxProps;
  seatButtonContainerProps?: BoxProps;
  seatButtonProps?: ButtonProps;
  ticketSummaryTextProps?: TextProps;
}
interface TicketSummaryMovieDetails {
  title: string;
  cinemaHall: string;
  show_start_date_time: string;
  poster: string;
  auditorium: string;
}

function Seats({
  seats,
  seatLabelProps,
  seatButtonContainerProps,
  seatButtonProps,
  ...props
}: Pick<
  TicketSummaryProps,
  "seats" | "seatLabelProps" | "seatButtonContainerProps" | "seatButtonProps"
> &
  BoxProps) {
  return (
    <Flex gap={{ base: "0.5rem", md: "1rem" }} {...props}>
      <Text
        fontSize={{ base: "sm", md: "lg" }}
        lineHeight={{ base: "2rem", md: "2.375rem" }}
        color="surface.400"
        flexShrink={0}
        {...seatLabelProps}
      >
        Seats:
      </Text>
      <Flex gap="0.5rem" flexWrap="wrap" {...seatButtonContainerProps}>
        {seats?.map((seat, index) => (
          <Button
            key={`seat__${index}_${seat}`}
            rounded="full"
            size="sm"
            aria-readonly
            colorScheme="primary"
            {...seatButtonProps}
          >
            {seat}
          </Button>
        ))}
      </Flex>
    </Flex>
  );
}

export function TicketSummary({
  movieDetails,
  headingText = "Ticket Summary",
  headingAction,
  seats,
  ticketSummaryTextProps,
  children,
  headingBoxProps,
  headingTitleProps,
  detailContainerProps,
  gridProps,
  posterGridItemProps,
  posterImageProps,
  movieDetailsGridItemProps,
  movieTitleProps,
  cinemaHallProps,
  showDateTimeProps,
  seatProps,
  ...props
}: TicketSummaryProps) {
  return (
    <Box
      shadow="sm"
      {...props}
      sx={{
        borderRadius: "md",
        overflow: "hidden",
      }}
    >
      <Flex
        background="surface.10"
        justifyContent="space-between"
        px={{ base: "1rem", md: "1.5rem" }}
        py="0.75rem"
        {...headingBoxProps}
      >
        <Heading
          color="surface.500"
          size={{ base: "sm", md: "md" }}
          {...ticketSummaryTextProps}
          {...headingTitleProps}
        >
          {headingText}
        </Heading>
        {headingAction}
      </Flex>
      <Flex
        direction="column"
        px={{ base: "1rem", md: "1.5rem" }}
        py={{ base: "1.25rem", md: "2rem" }}
        {...detailContainerProps}
      >
        <Grid
          templateColumns={{
            base: "repeat(4, 1fr)",
            md: "repeat(7, 1fr)",
          }}
          gap={{ base: "0.875rem", md: "1.25rem" }}
          {...gridProps}
        >
          <GridItem
            colSpan={1}
            sx={{ borderRadius: "sm", overflow: "hidden" }}
            {...posterGridItemProps}
          >
            <Image
              borderRadius="1rem"
              src={movieDetails.poster}
              alt={movieDetails.title}
              {...posterImageProps}
            />
          </GridItem>
          <GridItem
            colSpan={{
              base: 3,
              md: 5,
            }}
            minW={0}
            {...movieDetailsGridItemProps}
          >
            <Text
              as="h4"
              lineHeight={{ base: "1.6rem", md: "3rem" }}
              fontSize={{ base: "lg", md: "3xl" }}
              color="surface.500"
              fontWeight={600}
              {...ticketSummaryTextProps}
              {...movieTitleProps}
            >
              {movieDetails.title}
            </Text>
            <Text
              as="h6"
              fontSize={{ base: "sm", md: "lg" }}
              lineHeight={{ base: "1.4rem", md: "2.375rem" }}
              color="surface.500"
              fontWeight={500}
              {...ticketSummaryTextProps}
              {...cinemaHallProps}
            >
              {movieDetails.auditorium}, {movieDetails.cinemaHall}
            </Text>
            <Text
              as="p"
              lineHeight={{ base: "1.4rem", md: "1.875rem" }}
              fontSize={{ base: "sm", md: "md" }}
              color="surface.100"
              fontWeight={400}
              {...showDateTimeProps}
            >
              {movieDetails.show_start_date_time}
            </Text>
            <Seats seats={seats} mt="0.5rem" {...seatProps} />
          </GridItem>
        </Grid>
        {children}
      </Flex>
    </Box>
  );
}
